import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { 
  Settings as SettingsIcon, 
  Calendar, 
  Link2, 
  Unlink, 
  RefreshCw, 
  CheckCircle2, 
  XCircle,
  Mail,
  Clock,
  Loader2,
  UserPlus,
  Users,
  Edit2,
  Trash2,
  Building,
  CreditCard,
  Search,
  Plus,
  Repeat,
  DollarSign,
  CalendarClock,
  Play,
  Upload,
  FileSpreadsheet,
  Stethoscope,
  ToggleLeft,
  ToggleRight,
  Database,
  Download,
  Archive
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ScheduledReports from '../components/settings/ScheduledReports';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, token, hasRole } = useAuth();
  const [searchParams] = useSearchParams();
  const [calendarStatus, setCalendarStatus] = useState({
    connected: false,
    email: null,
    last_sync: null
  });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Doctors state
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(true);
  const [isDoctorModalOpen, setIsDoctorModalOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [deletingDoctor, setDeletingDoctor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [savingDoctor, setSavingDoctor] = useState(false);
  const [doctorSearch, setDoctorSearch] = useState('');
  
  // Recurring Expenses state
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [recurringLoading, setRecurringLoading] = useState(true);
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [deletingRecurring, setDeletingRecurring] = useState(null);
  const [deleteRecurringDialogOpen, setDeleteRecurringDialogOpen] = useState(false);
  const [savingRecurring, setSavingRecurring] = useState(false);
  const [pendingGeneration, setPendingGeneration] = useState({ pending_count: 0, current_period: '' });
  const [generating, setGenerating] = useState(false);

  // Exam Types state
  const [examTypes, setExamTypes] = useState([]);
  const [examTypesLoading, setExamTypesLoading] = useState(true);
  const [isExamTypeModalOpen, setIsExamTypeModalOpen] = useState(false);
  const [editingExamType, setEditingExamType] = useState(null);
  const [deletingExamType, setDeletingExamType] = useState(null);
  const [deleteExamTypeDialogOpen, setDeleteExamTypeDialogOpen] = useState(false);
  const [savingExamType, setSavingExamType] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const fileInputRef = useRef(null);

  // Database Export state (SUPERADMIN only)
  const [dbExportState, setDbExportState] = useState({
    exporting: false,
    exportId: null,
    progress: 0,
    message: '',
    status: 'idle', // idle, in_progress, completed, error
    downloadReady: false
  });
  const [collectionsInfo, setCollectionsInfo] = useState(null);
  const exportPollRef = useRef(null);
  
  const initialDoctorForm = {
    name: '',
    bank_name: '',
    bank_account_number: '',
    bank_account_type: '',
    phone: '',
    email: '',
    specialty: '',
    notes: ''
  };
  const [doctorForm, setDoctorForm] = useState(initialDoctorForm);
  
  const initialRecurringForm = {
    name: '',
    supplier: '',
    amount: '',
    payment_day: '1',
    scheduled_payment_date: '',
    notes: '',
    is_active: true
  };
  const [recurringForm, setRecurringForm] = useState(initialRecurringForm);

  const initialExamTypeForm = {
    name: '',
    code: '',
    default_price: '',
    default_duration: '30',
    is_active: true
  };
  const [examTypeForm, setExamTypeForm] = useState(initialExamTypeForm);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCalendarStatus();
    fetchDoctors();
    if (hasRole(['SUPERADMIN', 'ADMIN'])) {
      fetchRecurringExpenses();
      checkPendingGeneration();
      fetchExamTypes();
    }
    
    // Check for OAuth callback params
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'true') {
      toast.success('Google Calendar conectado exitosamente');
      fetchCalendarStatus();
    } else if (error) {
      toast.error('Error al conectar con Google Calendar');
    }
  }, [searchParams]);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/referring-doctors?active_only=false`, { headers });
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setDoctorsLoading(false);
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/calendar/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarStatus(response.data);
    } catch (error) {
      console.error('Error fetching calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectGoogleCalendar = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API_URL}/api/oauth/calendar/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      toast.error('Error al iniciar la conexión con Google Calendar');
      setConnecting(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    setDisconnecting(true);
    try {
      await axios.post(`${API_URL}/api/calendar/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCalendarStatus({
        connected: false,
        email: null,
        last_sync: null
      });
      toast.success('Google Calendar desconectado');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Error al desconectar Google Calendar');
    } finally {
      setDisconnecting(false);
    }
  };

  const syncAllAppointments = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/calendar/sync-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Sincronización completada: ${response.data.synced} citas sincronizadas`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} citas no pudieron sincronizarse`);
      }
      
      fetchCalendarStatus();
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Error al sincronizar citas');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-GT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', '');
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('es-GT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Database Export functions (SUPERADMIN only)
  const fetchCollectionsInfo = async () => {
    if (!hasRole('SUPERADMIN')) return;
    try {
      const response = await axios.get(`${API_URL}/api/database/collections/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollectionsInfo(response.data);
    } catch (error) {
      console.error('Error fetching collections info:', error);
    }
  };

  const startDatabaseExport = async () => {
    if (!hasRole('SUPERADMIN')) return;
    
    setDbExportState({
      exporting: true,
      exportId: null,
      progress: 0,
      message: 'Iniciando exportación...',
      status: 'in_progress',
      downloadReady: false
    });

    try {
      const response = await axios.post(`${API_URL}/api/database/export/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const exportId = response.data.export_id;
      setDbExportState(prev => ({ ...prev, exportId }));

      // Start polling for progress
      exportPollRef.current = setInterval(() => {
        checkExportProgress(exportId);
      }, 1000);

    } catch (error) {
      console.error('Error starting export:', error);
      toast.error('Error al iniciar la exportación');
      setDbExportState(prev => ({
        ...prev,
        exporting: false,
        status: 'error',
        message: 'Error al iniciar la exportación'
      }));
    }
  };

  const checkExportProgress = async (exportId) => {
    try {
      const response = await axios.get(`${API_URL}/api/database/export/progress/${exportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { status, progress, message, error } = response.data;

      setDbExportState(prev => ({
        ...prev,
        progress,
        message,
        status
      }));

      if (status === 'completed') {
        clearInterval(exportPollRef.current);
        setDbExportState(prev => ({
          ...prev,
          exporting: false,
          downloadReady: true
        }));
        toast.success('Exportación completada');
      } else if (status === 'error') {
        clearInterval(exportPollRef.current);
        setDbExportState(prev => ({
          ...prev,
          exporting: false,
          status: 'error',
          message: error || 'Error en la exportación'
        }));
        toast.error(error || 'Error en la exportación');
      }
    } catch (error) {
      console.error('Error checking progress:', error);
    }
  };

  const downloadExport = async () => {
    if (!dbExportState.exportId) return;

    try {
      const response = await axios.get(
        `${API_URL}/api/database/export/download/${dbExportState.exportId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `oftalmodiagnosis_backup_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Cleanup on server
      await axios.delete(`${API_URL}/api/database/export/cleanup/${dbExportState.exportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Reset state
      setDbExportState({
        exporting: false,
        exportId: null,
        progress: 0,
        message: '',
        status: 'idle',
        downloadReady: false
      });

      toast.success('Backup descargado exitosamente');
    } catch (error) {
      console.error('Error downloading export:', error);
      toast.error('Error al descargar el backup');
    }
  };

  // Cleanup poll on unmount
  useEffect(() => {
    return () => {
      if (exportPollRef.current) {
        clearInterval(exportPollRef.current);
      }
    };
  }, []);

  // Fetch collections info for SUPERADMIN
  useEffect(() => {
    if (hasRole('SUPERADMIN')) {
      fetchCollectionsInfo();
    }
  }, [token]);

  // Doctor CRUD functions
  const openCreateDoctorModal = () => {
    setEditingDoctor(null);
    setDoctorForm(initialDoctorForm);
    setIsDoctorModalOpen(true);
  };

  const openEditDoctorModal = (doctor) => {
    setEditingDoctor(doctor);
    setDoctorForm({
      name: doctor.name || '',
      bank_name: doctor.bank_name || '',
      bank_account_number: doctor.bank_account_number || '',
      bank_account_type: doctor.bank_account_type || '',
      phone: doctor.phone || '',
      email: doctor.email || '',
      specialty: doctor.specialty || '',
      notes: doctor.notes || ''
    });
    setIsDoctorModalOpen(true);
  };

  const handleSaveDoctor = async (e) => {
    e.preventDefault();
    
    if (!doctorForm.name.trim()) {
      toast.error('El nombre del médico es requerido');
      return;
    }

    setSavingDoctor(true);
    try {
      if (editingDoctor) {
        await axios.put(`${API_URL}/api/referring-doctors/${editingDoctor.id}`, doctorForm, { headers });
        toast.success('Médico actualizado');
      } else {
        await axios.post(`${API_URL}/api/referring-doctors`, doctorForm, { headers });
        toast.success('Médico registrado');
      }
      setIsDoctorModalOpen(false);
      fetchDoctors();
    } catch (error) {
      console.error('Error saving doctor:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al guardar');
      }
    } finally {
      setSavingDoctor(false);
    }
  };

  const handleDeleteDoctor = async () => {
    if (!deletingDoctor) return;
    
    try {
      await axios.delete(`${API_URL}/api/referring-doctors/${deletingDoctor.id}`, { headers });
      toast.success('Médico eliminado');
      setDeleteDialogOpen(false);
      setDeletingDoctor(null);
      fetchDoctors();
    } catch (error) {
      console.error('Error deleting doctor:', error);
      toast.error('Error al eliminar');
    }
  };

  const filteredDoctors = doctors.filter(doctor =>
    doctor.name.toLowerCase().includes(doctorSearch.toLowerCase()) ||
    (doctor.bank_name && doctor.bank_name.toLowerCase().includes(doctorSearch.toLowerCase()))
  );

  // Recurring Expenses CRUD functions
  const fetchRecurringExpenses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/recurring-expenses?active_only=false`, { headers });
      setRecurringExpenses(response.data);
    } catch (error) {
      console.error('Error fetching recurring expenses:', error);
    } finally {
      setRecurringLoading(false);
    }
  };

  const checkPendingGeneration = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/recurring-expenses/check-pending`, { headers });
      setPendingGeneration(response.data);
    } catch (error) {
      console.error('Error checking pending generation:', error);
    }
  };

  const openCreateRecurringModal = () => {
    setEditingRecurring(null);
    setRecurringForm(initialRecurringForm);
    setIsRecurringModalOpen(true);
  };

  const openEditRecurringModal = (expense) => {
    setEditingRecurring(expense);
    setRecurringForm({
      name: expense.name || '',
      supplier: expense.supplier || '',
      amount: expense.amount?.toString() || '',
      payment_day: expense.payment_day?.toString() || '1',
      scheduled_payment_date: expense.scheduled_payment_date || '',
      notes: expense.notes || '',
      is_active: expense.is_active !== false
    });
    setIsRecurringModalOpen(true);
  };

  const handleSaveRecurring = async (e) => {
    e.preventDefault();
    
    if (!recurringForm.name.trim() || !recurringForm.supplier.trim() || !recurringForm.amount) {
      toast.error('Complete todos los campos requeridos');
      return;
    }

    setSavingRecurring(true);
    try {
      const data = {
        ...recurringForm,
        amount: parseFloat(recurringForm.amount),
        payment_day: parseInt(recurringForm.payment_day),
        scheduled_payment_date: recurringForm.scheduled_payment_date || null
      };
      
      if (editingRecurring) {
        await axios.put(`${API_URL}/api/recurring-expenses/${editingRecurring.id}`, data, { headers });
        toast.success('Gasto fijo actualizado');
      } else {
        await axios.post(`${API_URL}/api/recurring-expenses`, data, { headers });
        toast.success('Gasto fijo registrado');
      }
      setIsRecurringModalOpen(false);
      fetchRecurringExpenses();
    } catch (error) {
      console.error('Error saving recurring expense:', error);
      toast.error('Error al guardar');
    } finally {
      setSavingRecurring(false);
    }
  };

  const handleDeleteRecurring = async () => {
    if (!deletingRecurring) return;
    
    try {
      await axios.delete(`${API_URL}/api/recurring-expenses/${deletingRecurring.id}`, { headers });
      toast.success('Gasto fijo eliminado');
      setDeleteRecurringDialogOpen(false);
      setDeletingRecurring(null);
      fetchRecurringExpenses();
    } catch (error) {
      console.error('Error deleting recurring expense:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleGenerateMonthlyExpenses = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/recurring-expenses/generate-monthly`, {}, { headers });
      toast.success(`${response.data.generated} gastos generados para ${response.data.period}`);
      checkPendingGeneration();
    } catch (error) {
      console.error('Error generating expenses:', error);
      toast.error('Error al generar gastos');
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
    }).format(amount || 0);
  };

  // Exam Types CRUD functions
  const fetchExamTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/exam-types?active_only=false`, { headers });
      setExamTypes(response.data);
    } catch (error) {
      console.error('Error fetching exam types:', error);
    } finally {
      setExamTypesLoading(false);
    }
  };

  const openCreateExamTypeModal = () => {
    setEditingExamType(null);
    setExamTypeForm(initialExamTypeForm);
    setIsExamTypeModalOpen(true);
  };

  const openEditExamTypeModal = (examType) => {
    setEditingExamType(examType);
    setExamTypeForm({
      name: examType.name || '',
      code: examType.code || '',
      default_price: examType.default_price?.toString() || '',
      default_duration: examType.default_duration?.toString() || '30',
      is_active: examType.is_active !== false
    });
    setIsExamTypeModalOpen(true);
  };

  const handleSaveExamType = async (e) => {
    e.preventDefault();
    
    if (!examTypeForm.name.trim()) {
      toast.error('El nombre del examen es requerido');
      return;
    }

    setSavingExamType(true);
    try {
      const data = {
        ...examTypeForm,
        default_price: parseFloat(examTypeForm.default_price) || 0,
        default_duration: parseInt(examTypeForm.default_duration) || 30
      };
      
      if (editingExamType) {
        await axios.put(`${API_URL}/api/exam-types/${editingExamType.id}`, data, { headers });
        toast.success('Tipo de examen actualizado');
      } else {
        await axios.post(`${API_URL}/api/exam-types`, data, { headers });
        toast.success('Tipo de examen creado');
      }
      setIsExamTypeModalOpen(false);
      fetchExamTypes();
    } catch (error) {
      console.error('Error saving exam type:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingExamType(false);
    }
  };

  const handleDeleteExamType = async () => {
    if (!deletingExamType) return;
    
    try {
      await axios.delete(`${API_URL}/api/exam-types/${deletingExamType.id}`, { headers });
      toast.success('Tipo de examen eliminado');
      setDeleteExamTypeDialogOpen(false);
      setDeletingExamType(null);
      fetchExamTypes();
    } catch (error) {
      console.error('Error deleting exam type:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleToggleExamTypeActive = async (examType) => {
    try {
      await axios.post(`${API_URL}/api/exam-types/toggle-active/${examType.id}`, {}, { headers });
      toast.success(`Examen ${examType.is_active ? 'desactivado' : 'activado'}`);
      fetchExamTypes();
    } catch (error) {
      console.error('Error toggling exam type:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingExcel(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/exam-types/upload-excel`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`${response.data.imported} tipos de examen importados`);
      if (response.data.skipped > 0) {
        toast.info(`${response.data.skipped} ya existentes (omitidos)`);
      }
      fetchExamTypes();
    } catch (error) {
      console.error('Error uploading excel:', error);
      toast.error(error.response?.data?.detail || 'Error al importar archivo');
    } finally {
      setUploadingExcel(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a5192]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-8 h-8 text-[#1a5192]" />
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Configuración</h2>
          <p className="text-sm text-slate-500">Gestiona las integraciones del sistema</p>
        </div>
      </div>

      {/* Google Calendar Integration */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center border border-slate-100">
                <Calendar className="w-7 h-7 text-[#4285f4]" />
              </div>
              <div>
                <CardTitle className="text-lg">Google Calendar</CardTitle>
                <CardDescription>Sincroniza las citas con tu calendario de Google</CardDescription>
              </div>
            </div>
            {calendarStatus.connected ? (
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                Conectado
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                <XCircle className="w-3.5 h-3.5 mr-1.5" />
                No conectado
              </Badge>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {calendarStatus.connected ? (
            <>
              {/* Connected State */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Cuenta conectada</p>
                    <p className="font-medium text-slate-900">{calendarStatus.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Última sincronización</p>
                    <p className="font-medium text-slate-900">{formatDate(calendarStatus.last_sync)}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={syncAllAppointments}
                  disabled={syncing}
                  className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white"
                  data-testid="sync-all-button"
                >
                  {syncing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Sincronizar todas las citas
                </Button>
                
                <Button
                  onClick={disconnectGoogleCalendar}
                  disabled={disconnecting}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="disconnect-button"
                >
                  {disconnecting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Unlink className="w-4 h-4 mr-2" />
                  )}
                  Desconectar cuenta
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Not Connected State */}
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Conecta tu cuenta de Google para sincronizar automáticamente las citas con tu calendario.
                  Las citas creadas se agregarán con recordatorios de 30 minutos.
                </p>
              </div>

              <Button
                onClick={connectGoogleCalendar}
                disabled={connecting}
                className="bg-[#4285f4] hover:bg-[#3367d6] text-white"
                data-testid="connect-button"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" />
                )}
                Conectar Google Calendar
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-slate-200 shadow-sm bg-slate-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-slate-900 mb-3">Información sobre la sincronización</h3>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              Las nuevas citas se crean automáticamente en Google Calendar
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              Los cambios en las citas se actualizan en tiempo real
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              Las citas canceladas se eliminan del calendario
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              Recordatorio automático 30 minutos antes de cada cita
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Referring Doctors Management */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#1a5192]/10 rounded-lg flex items-center justify-center">
                <Users className="w-7 h-7 text-[#1a5192]" />
              </div>
              <div>
                <CardTitle className="text-lg">Médicos Referentes</CardTitle>
                <CardDescription>Gestiona la base de datos de médicos con información bancaria</CardDescription>
              </div>
            </div>
            <Button 
              onClick={openCreateDoctorModal}
              className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white"
              data-testid="new-doctor-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Nuevo Médico
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Buscar médico por nombre o banco..."
              value={doctorSearch}
              onChange={(e) => setDoctorSearch(e.target.value)}
              className="pl-10 bg-slate-50"
              data-testid="search-doctors"
            />
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table data-testid="doctors-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-700">Nombre</TableHead>
                  <TableHead className="font-semibold text-slate-700">Banco</TableHead>
                  <TableHead className="font-semibold text-slate-700">No. Cuenta</TableHead>
                  <TableHead className="font-semibold text-slate-700">Tipo Cuenta</TableHead>
                  <TableHead className="font-semibold text-slate-700">Teléfono</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {doctorsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : filteredDoctors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                      {doctorSearch ? 'No se encontraron médicos' : 'No hay médicos registrados'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDoctors.map((doctor) => (
                    <TableRow key={doctor.id} className="hover:bg-slate-50" data-testid={`doctor-row-${doctor.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{doctor.name}</p>
                          {doctor.specialty && (
                            <p className="text-xs text-slate-500">{doctor.specialty}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {doctor.bank_name ? (
                          <span className="flex items-center gap-1.5">
                            <Building className="w-4 h-4 text-slate-400" />
                            {doctor.bank_name}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {doctor.bank_account_number ? (
                          <span className="font-mono text-sm">{doctor.bank_account_number}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {doctor.bank_account_type ? (
                          <Badge variant="outline" className="border-slate-300">
                            {doctor.bank_account_type}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {doctor.phone || <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDoctorModal(doctor)}
                            className="h-8 w-8 text-slate-500 hover:text-[#1a5192] hover:bg-[#1a5192]/10"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setDeletingDoctor(doctor); setDeleteDialogOpen(true); }}
                            className="h-8 w-8 text-slate-500 hover:text-red-500 hover:bg-red-50"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          <p className="text-xs text-slate-500">
            Los médicos registrados aquí aparecerán en el autocompletado y sus datos bancarios se incluirán en los reportes de comisiones.
          </p>
        </CardContent>
      </Card>

      {/* Doctor Modal */}
      <Dialog open={isDoctorModalOpen} onOpenChange={setIsDoctorModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="doctor-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1a5192]" />
              {editingDoctor ? 'Editar Médico' : 'Nuevo Médico Referente'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveDoctor} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="doctor_name">Nombre del Médico *</Label>
              <Input
                id="doctor_name"
                value={doctorForm.name}
                onChange={(e) => setDoctorForm({ ...doctorForm, name: e.target.value })}
                placeholder="Dr. Juan Pérez"
                className="bg-slate-50"
                data-testid="doctor-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="doctor_specialty">Especialidad</Label>
                <Input
                  id="doctor_specialty"
                  value={doctorForm.specialty}
                  onChange={(e) => setDoctorForm({ ...doctorForm, specialty: e.target.value })}
                  placeholder="Oftalmología"
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="doctor_phone">Teléfono</Label>
                <Input
                  id="doctor_phone"
                  value={doctorForm.phone}
                  onChange={(e) => setDoctorForm({ ...doctorForm, phone: e.target.value })}
                  placeholder="5555-1234"
                  className="bg-slate-50"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="doctor_email">Correo Electrónico</Label>
              <Input
                id="doctor_email"
                type="email"
                value={doctorForm.email}
                onChange={(e) => setDoctorForm({ ...doctorForm, email: e.target.value })}
                placeholder="doctor@email.com"
                className="bg-slate-50"
              />
            </div>
            
            {/* Bank Info Section */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-slate-800 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Información Bancaria
              </h4>
              
              <div className="space-y-2">
                <Label htmlFor="bank_name">Nombre del Banco</Label>
                <Input
                  id="bank_name"
                  value={doctorForm.bank_name}
                  onChange={(e) => setDoctorForm({ ...doctorForm, bank_name: e.target.value })}
                  placeholder="Banco Industrial, G&T Continental, etc."
                  className="bg-white"
                  data-testid="doctor-bank-name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_account_number">Número de Cuenta</Label>
                  <Input
                    id="bank_account_number"
                    value={doctorForm.bank_account_number}
                    onChange={(e) => setDoctorForm({ ...doctorForm, bank_account_number: e.target.value })}
                    placeholder="0123456789"
                    className="bg-white font-mono"
                    data-testid="doctor-account-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Cuenta</Label>
                  <Select 
                    value={doctorForm.bank_account_type} 
                    onValueChange={(value) => setDoctorForm({ ...doctorForm, bank_account_type: value })}
                  >
                    <SelectTrigger className="bg-white" data-testid="doctor-account-type">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ahorro">Ahorro</SelectItem>
                      <SelectItem value="Monetaria">Monetaria</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="doctor_notes">Notas</Label>
              <Textarea
                id="doctor_notes"
                value={doctorForm.notes}
                onChange={(e) => setDoctorForm({ ...doctorForm, notes: e.target.value })}
                placeholder="Observaciones adicionales..."
                className="bg-slate-50 resize-none"
                rows={2}
              />
            </div>
            
            <DialogFooter className="gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsDoctorModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit"
                className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white"
                data-testid="save-doctor-button"
                disabled={savingDoctor}
              >
                {savingDoctor ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                ) : (
                  editingDoctor ? 'Actualizar' : 'Guardar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Doctor Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este médico?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingDoctor && (
                <>
                  Está a punto de eliminar al médico "<strong>{deletingDoctor.name}</strong>".
                  <br /><br />
                  Esta acción no afectará los registros de pacientes existentes.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDoctor}
              className="bg-red-500 hover:bg-red-600"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recurring Expenses Section - Admin/SuperAdmin Only */}
      {hasRole(['SUPERADMIN', 'ADMIN']) && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Repeat className="w-7 h-7 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Gastos Fijos Mensuales</CardTitle>
                  <CardDescription>Configura gastos recurrentes que se cargan automáticamente cada mes</CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                {pendingGeneration.pending_count > 0 && (
                  <Button 
                    onClick={handleGenerateMonthlyExpenses}
                    disabled={generating}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="generate-expenses-button"
                  >
                    {generating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Generar {pendingGeneration.current_period}
                  </Button>
                )}
                <Button 
                  onClick={openCreateRecurringModal}
                  className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white"
                  data-testid="new-recurring-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Gasto Fijo
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {pendingGeneration.pending_count > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>{pendingGeneration.pending_count}</strong> gastos fijos pendientes de generar para <strong>{pendingGeneration.current_period}</strong>.
                  Haz clic en "Generar" para crear los gastos del mes actual.
                </p>
              </div>
            )}

            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table data-testid="recurring-expenses-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Gasto</TableHead>
                    <TableHead className="font-semibold text-slate-700">Proveedor</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Monto</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Día Generación</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Fecha de Pago</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Estado</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recurringLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : recurringExpenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                        No hay gastos fijos configurados
                      </TableCell>
                    </TableRow>
                  ) : (
                    recurringExpenses.map((expense) => (
                      <TableRow key={expense.id} className="hover:bg-slate-50" data-testid={`recurring-row-${expense.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-amber-500" />
                            <span className="font-medium text-slate-900">{expense.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1.5">
                            <Building className="w-4 h-4 text-slate-400" />
                            {expense.supplier}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">
                          {formatCurrency(expense.amount)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="border-slate-300">
                            <CalendarClock className="w-3 h-3 mr-1" />
                            Día {expense.payment_day}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {expense.scheduled_payment_date ? (
                            <Badge variant="outline" className="border-blue-300 text-blue-700">
                              <Calendar className="w-3 h-3 mr-1" />
                              {formatDateShort(expense.scheduled_payment_date)}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {expense.is_active ? (
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                              Activo
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                              Inactivo
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditRecurringModal(expense)}
                              className="h-8 w-8 text-slate-500 hover:text-[#1a5192] hover:bg-[#1a5192]/10"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setDeletingRecurring(expense); setDeleteRecurringDialogOpen(true); }}
                              className="h-8 w-8 text-slate-500 hover:text-red-500 hover:bg-red-50"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            <p className="text-xs text-slate-500">
              Los gastos fijos configurados se generan automáticamente en el módulo de Gastos cada mes, en la fecha de pago configurada.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Recurring Expense Modal */}
      <Dialog open={isRecurringModalOpen} onOpenChange={setIsRecurringModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="recurring-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Repeat className="w-5 h-5 text-amber-500" />
              {editingRecurring ? 'Editar Gasto Fijo' : 'Nuevo Gasto Fijo'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveRecurring} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="recurring_name">Nombre del Gasto *</Label>
              <Input
                id="recurring_name"
                value={recurringForm.name}
                onChange={(e) => setRecurringForm({ ...recurringForm, name: e.target.value })}
                placeholder="Ej: Alquiler, Luz, Internet"
                className="bg-slate-50"
                data-testid="recurring-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recurring_supplier">Proveedor *</Label>
              <Input
                id="recurring_supplier"
                value={recurringForm.supplier}
                onChange={(e) => setRecurringForm({ ...recurringForm, supplier: e.target.value })}
                placeholder="Nombre del proveedor"
                className="bg-slate-50"
                data-testid="recurring-supplier"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="recurring_amount">Monto Mensual *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">Q</span>
                  <Input
                    id="recurring_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={recurringForm.amount}
                    onChange={(e) => setRecurringForm({ ...recurringForm, amount: e.target.value })}
                    placeholder="0.00"
                    className="bg-slate-50 pl-8"
                    data-testid="recurring-amount"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Día de Generación *</Label>
                <Select 
                  value={recurringForm.payment_day} 
                  onValueChange={(value) => setRecurringForm({ ...recurringForm, payment_day: value })}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="recurring-payment-day">
                    <SelectValue placeholder="Día" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(28)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        Día {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="recurring_scheduled_date">Fecha de Pago Programada</Label>
              <Input
                id="recurring_scheduled_date"
                type="date"
                value={recurringForm.scheduled_payment_date}
                onChange={(e) => setRecurringForm({ ...recurringForm, scheduled_payment_date: e.target.value })}
                className="bg-slate-50"
                data-testid="recurring-scheduled-date"
              />
              <p className="text-xs text-slate-500">Opcional: Fecha específica para recordar el pago</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recurring_notes">Notas</Label>
              <Textarea
                id="recurring_notes"
                value={recurringForm.notes}
                onChange={(e) => setRecurringForm({ ...recurringForm, notes: e.target.value })}
                placeholder="Observaciones adicionales..."
                className="bg-slate-50 resize-none"
                rows={2}
              />
            </div>
            
            {editingRecurring && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="recurring_active"
                  checked={recurringForm.is_active}
                  onChange={(e) => setRecurringForm({ ...recurringForm, is_active: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="recurring_active" className="text-sm font-normal">
                  Gasto activo (se genera cada mes)
                </Label>
              </div>
            )}
            
            <DialogFooter className="gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsRecurringModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="save-recurring-button"
                disabled={savingRecurring}
              >
                {savingRecurring ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                ) : (
                  editingRecurring ? 'Actualizar' : 'Guardar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Recurring Expense Confirmation */}
      <AlertDialog open={deleteRecurringDialogOpen} onOpenChange={setDeleteRecurringDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este gasto fijo?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingRecurring && (
                <>
                  Está a punto de eliminar el gasto fijo "<strong>{deletingRecurring.name}</strong>" de <strong>{deletingRecurring.supplier}</strong>.
                  <br /><br />
                  Los gastos ya generados no serán afectados.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRecurring}
              className="bg-red-500 hover:bg-red-600"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Exam Types Section - Admin/SuperAdmin Only */}
      {hasRole(['SUPERADMIN', 'ADMIN']) && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-7 h-7 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Tipos de Examen</CardTitle>
                  <CardDescription>Configura los tipos de examen disponibles para selección</CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleExcelUpload}
                  accept=".xlsx,.xls"
                  className="hidden"
                />
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingExcel}
                  variant="outline"
                  className="border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                  data-testid="upload-excel-button"
                >
                  {uploadingExcel ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Cargar Excel
                </Button>
                <Button 
                  onClick={openCreateExamTypeModal}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="new-exam-type-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Examen
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-800">
                <strong>Formato Excel:</strong> El archivo debe tener una columna <code className="bg-purple-100 px-1 rounded">nombre</code> o <code className="bg-purple-100 px-1 rounded">tipo</code>. 
                Opcionalmente puede incluir: <code className="bg-purple-100 px-1 rounded">codigo</code>, <code className="bg-purple-100 px-1 rounded">precio</code>, <code className="bg-purple-100 px-1 rounded">duracion</code> (minutos).
              </p>
            </div>

            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table data-testid="exam-types-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Nombre</TableHead>
                    <TableHead className="font-semibold text-slate-700">Código</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Precio Sugerido</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Duración</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Estado</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {examTypesLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : examTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                        No hay tipos de examen configurados. Sube un archivo Excel o agrega manualmente.
                      </TableCell>
                    </TableRow>
                  ) : (
                    examTypes.map((examType) => (
                      <TableRow key={examType.id} className="hover:bg-slate-50" data-testid={`exam-type-row-${examType.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Stethoscope className="w-4 h-4 text-purple-500" />
                            <span className="font-medium text-slate-900">{examType.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">
                          {examType.code || '-'}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">
                          {examType.default_price > 0 ? formatCurrency(examType.default_price) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="border-slate-300">
                            {examType.default_duration} min
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <button
                            onClick={() => handleToggleExamTypeActive(examType)}
                            className="inline-flex items-center"
                            title={examType.is_active ? 'Clic para desactivar' : 'Clic para activar'}
                          >
                            {examType.is_active ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 cursor-pointer">
                                <ToggleRight className="w-3 h-3 mr-1" />
                                Activo
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-200 cursor-pointer">
                                <ToggleLeft className="w-3 h-3 mr-1" />
                                Inactivo
                              </Badge>
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditExamTypeModal(examType)}
                              className="h-8 w-8 text-slate-500 hover:text-purple-600 hover:bg-purple-50"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setDeletingExamType(examType); setDeleteExamTypeDialogOpen(true); }}
                              className="h-8 w-8 text-slate-500 hover:text-red-500 hover:bg-red-50"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            <p className="text-xs text-slate-500">
              Solo los tipos de examen activos aparecerán disponibles para selección al crear citas y registrar pacientes.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Exam Type Modal */}
      <Dialog open={isExamTypeModalOpen} onOpenChange={setIsExamTypeModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="exam-type-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-purple-500" />
              {editingExamType ? 'Editar Tipo de Examen' : 'Nuevo Tipo de Examen'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveExamType} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="exam_name">Nombre del Examen *</Label>
              <Input
                id="exam_name"
                value={examTypeForm.name}
                onChange={(e) => setExamTypeForm({ ...examTypeForm, name: e.target.value })}
                placeholder="Ej: OCT Macular, Refracción, Fondo de Ojo"
                className="bg-slate-50"
                data-testid="exam-type-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="exam_code">Código (opcional)</Label>
              <Input
                id="exam_code"
                value={examTypeForm.code}
                onChange={(e) => setExamTypeForm({ ...examTypeForm, code: e.target.value })}
                placeholder="Código interno"
                className="bg-slate-50"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exam_price">Precio Sugerido</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">Q</span>
                  <Input
                    id="exam_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={examTypeForm.default_price}
                    onChange={(e) => setExamTypeForm({ ...examTypeForm, default_price: e.target.value })}
                    placeholder="0.00"
                    className="bg-slate-50 pl-8"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="exam_duration">Duración (minutos)</Label>
                <Input
                  id="exam_duration"
                  type="number"
                  min="5"
                  max="180"
                  step="5"
                  value={examTypeForm.default_duration}
                  onChange={(e) => setExamTypeForm({ ...examTypeForm, default_duration: e.target.value })}
                  className="bg-slate-50"
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsExamTypeModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit"
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="save-exam-type-button"
                disabled={savingExamType}
              >
                {savingExamType ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                ) : (
                  editingExamType ? 'Actualizar' : 'Guardar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Exam Type Confirmation */}
      <AlertDialog open={deleteExamTypeDialogOpen} onOpenChange={setDeleteExamTypeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este tipo de examen?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingExamType && (
                <>
                  Está a punto de eliminar el tipo de examen "<strong>{deletingExamType.name}</strong>".
                  <br /><br />
                  Los registros de pacientes existentes no serán afectados.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteExamType}
              className="bg-red-500 hover:bg-red-600"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Scheduled Reports Section - SuperAdmin Only */}
      {hasRole('SUPERADMIN') && (
        <ScheduledReports />
      )}

      {/* Database Export Section - SuperAdmin Only */}
      {hasRole('SUPERADMIN') && (
        <Card className="border-slate-200 shadow-sm border-l-4 border-l-red-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <Database className="w-7 h-7 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  Exportar Base de Datos
                  <Badge className="bg-red-100 text-red-700 text-xs">SUPERADMIN</Badge>
                </CardTitle>
                <CardDescription>
                  Descarga una copia completa de la base de datos en formato ZIP (archivos JSON)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Collections Info */}
            {collectionsInfo && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-medium text-sm text-slate-700 mb-3">Información de la Base de Datos</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white p-3 rounded border border-slate-100">
                    <p className="text-xs text-slate-500">Base de datos</p>
                    <p className="font-semibold text-slate-800">{collectionsInfo.database}</p>
                  </div>
                  <div className="bg-white p-3 rounded border border-slate-100">
                    <p className="text-xs text-slate-500">Total Colecciones</p>
                    <p className="font-semibold text-slate-800">{collectionsInfo.total_collections}</p>
                  </div>
                  <div className="col-span-2 bg-white p-3 rounded border border-slate-100">
                    <p className="text-xs text-slate-500 mb-2">Colecciones</p>
                    <div className="flex flex-wrap gap-1">
                      {collectionsInfo.collections.map((col, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {col.name} ({col.count})
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Export Progress */}
            {dbExportState.exporting && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3 mb-3">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  <span className="font-medium text-blue-800">Exportando...</span>
                </div>
                <Progress value={dbExportState.progress} className="h-3 mb-2" />
                <p className="text-sm text-blue-600">{dbExportState.message}</p>
                <p className="text-xs text-blue-500 mt-1">{dbExportState.progress}% completado</p>
              </div>
            )}

            {/* Download Ready */}
            {dbExportState.downloadReady && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Exportación completada</p>
                      <p className="text-sm text-green-600">El archivo está listo para descargar</p>
                    </div>
                  </div>
                  <Button
                    onClick={downloadExport}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar ZIP
                  </Button>
                </div>
              </div>
            )}

            {/* Error State */}
            {dbExportState.status === 'error' && (
              <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-3">
                  <XCircle className="w-6 h-6 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Error en la exportación</p>
                    <p className="text-sm text-red-600">{dbExportState.message}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Export Button */}
            {!dbExportState.exporting && !dbExportState.downloadReady && (
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <p className="font-medium text-slate-700">Generar Backup Completo</p>
                  <p className="text-sm text-slate-500">
                    Incluye todas las colecciones: pacientes, citas, gastos, usuarios, etc.
                  </p>
                </div>
                <Button
                  onClick={startDatabaseExport}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={dbExportState.exporting}
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Iniciar Exportación
                </Button>
              </div>
            )}

            <p className="text-xs text-slate-400 mt-4">
              * La exportación puede tardar varios minutos dependiendo del tamaño de la base de datos.
              El archivo ZIP contendrá archivos JSON de cada colección.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Settings;
